'use client'

import { useState, useEffect, useRef, useCallback } from 'react'
import { MapPin, Navigation, Loader2, X, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  geocodeZipCode,
  searchCities,
  isValidZipCode,
  looksLikeCity,
  reverseGeocode,
  Citysuggestion,
} from '@/lib/geocoding'
import {
  StoredLocation,
  setStoredLocation,
  clearStoredLocation,
  getBrowserLocation,
} from '@/lib/location'

interface LocationPickerProps {
  currentLocation?: {
    latitude: number
    longitude: number
    label?: string
    source?: string
  }
  onLocationChange: (location: StoredLocation) => void
}

export default function LocationPicker({
  currentLocation,
  onLocationChange,
}: LocationPickerProps) {
  const [open, setOpen] = useState(false)
  const [locationInput, setLocationInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [geoLoading, setGeoLoading] = useState(false)

  // Typeahead state
  const [suggestions, setSuggestions] = useState<Citysuggestion[]>([])
  const [suggestionsLoading, setSuggestionsLoading] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const [dropdownOpen, setDropdownOpen] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Debounced city search — only fires for non-zip input with ≥2 chars
  const fetchSuggestions = useCallback((value: string) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current)

    const trimmed = value.trim()
    if (!looksLikeCity(trimmed) || trimmed.length < 2) {
      setSuggestions([])
      setDropdownOpen(false)
      return
    }

    debounceTimer.current = setTimeout(async () => {
      setSuggestionsLoading(true)
      const results = await searchCities(trimmed, 5)
      setSuggestions(results)
      setDropdownOpen(results.length > 0)
      setActiveIndex(-1)
      setSuggestionsLoading(false)
    }, 300)
  }, [])

  useEffect(() => {
    fetchSuggestions(locationInput)
  }, [locationInput, fetchSuggestions])

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [])

  const applyResult = (suggestion: Citysuggestion) => {
    const { result, label } = suggestion
    const location: StoredLocation = {
      latitude: result.latitude,
      longitude: result.longitude,
      label,
      source: 'zip',
    }
    setStoredLocation(location)
    onLocationChange(location)
    setOpen(false)
    setLocationInput('')
    setSuggestions([])
    setDropdownOpen(false)
  }

  const handleLocationSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // If a suggestion is highlighted, select it
    if (dropdownOpen && activeIndex >= 0 && suggestions[activeIndex]) {
      applyResult(suggestions[activeIndex])
      return
    }

    const input = locationInput.trim()
    if (!input) {
      setError('Please enter a zip code or city name')
      return
    }

    setLoading(true)
    try {
      let result = null

      if (!looksLikeCity(input)) {
        // All-digit input → treat as zip
        if (!isValidZipCode(input)) {
          setError('Please enter a valid 5-digit zip code')
          setLoading(false)
          return
        }
        result = await geocodeZipCode(input)
        if (!result) {
          setError('Could not find location for this zip code')
        }
      } else {
        // City name — use first suggestion if available, otherwise geocode directly
        if (suggestions.length > 0) {
          applyResult(suggestions[0])
          setLoading(false)
          return
        }
        const cities = await searchCities(input, 1)
        if (cities.length > 0) {
          applyResult(cities[0])
          setLoading(false)
          return
        }
        setError('Could not find that city. Try adding the state (e.g. "Austin, TX").')
      }

      if (result) {
        const location: StoredLocation = {
          latitude: result.latitude,
          longitude: result.longitude,
          label: `${result.city}, ${result.state}`,
          source: 'zip',
        }
        setStoredLocation(location)
        onLocationChange(location)
        setOpen(false)
        setLocationInput('')
      }
    } catch {
      setError('Failed to look up location. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!dropdownOpen || suggestions.length === 0) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex(prev => Math.min(prev + 1, suggestions.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex(prev => Math.max(prev - 1, -1))
    } else if (e.key === 'Escape') {
      setDropdownOpen(false)
      setActiveIndex(-1)
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault()
      applyResult(suggestions[activeIndex])
    }
  }

  const handleUseMyLocation = async () => {
    setError(null)
    setGeoLoading(true)

    try {
      const browserLoc = await getBrowserLocation()
      if (browserLoc) {
        // Reverse geocode to get a human-readable city label
        const cityLabel = await reverseGeocode(browserLoc.latitude, browserLoc.longitude)
        const location: StoredLocation = {
          latitude: browserLoc.latitude,
          longitude: browserLoc.longitude,
          label: cityLabel ?? 'Current Location',
          source: 'geolocation',
        }
        setStoredLocation(location)
        onLocationChange(location)
        setOpen(false)
      } else {
        setError('Could not get your location. Please check your browser permissions.')
      }
    } catch {
      setError('Failed to get location. Please try again or enter a city / zip code.')
    } finally {
      setGeoLoading(false)
    }
  }

  const handleReset = () => {
    clearStoredLocation()
    window.location.reload()
  }

  const isOverridden =
    currentLocation?.source === 'zip' || currentLocation?.source === 'geolocation'

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 transition group">
          <MapPin size={14} className="text-slate-400 group-hover:text-slate-600" />
          <span className="max-w-[150px] truncate">
            {currentLocation?.label || 'Set Location'}
          </span>
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Set Your Location</DialogTitle>
          <DialogDescription>
            Enter a city name or zip code to find nearby theaters.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Location Form with typeahead */}
          <form onSubmit={handleLocationSubmit} className="space-y-3">
            <div className="relative flex gap-2">
              <div className="relative flex-1">
                <Input
                  ref={inputRef}
                  type="text"
                  placeholder="City (e.g. Austin, TX) or zip code"
                  value={locationInput}
                  onChange={e => {
                    setLocationInput(e.target.value)
                    setError(null)
                  }}
                  onKeyDown={handleKeyDown}
                  onFocus={() => {
                    if (suggestions.length > 0) setDropdownOpen(true)
                  }}
                  autoComplete="off"
                  className="w-full"
                />

                {/* Loading spinner inside input */}
                {suggestionsLoading && (
                  <Loader2
                    size={14}
                    className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400 pointer-events-none"
                  />
                )}

                {/* Suggestions dropdown */}
                {dropdownOpen && suggestions.length > 0 && (
                  <div
                    ref={dropdownRef}
                    className="absolute z-50 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg overflow-hidden"
                  >
                    {suggestions.map((s, i) => (
                      <button
                        key={s.label}
                        type="button"
                        onMouseDown={e => {
                          e.preventDefault() // keep focus on input
                          applyResult(s)
                        }}
                        onMouseEnter={() => setActiveIndex(i)}
                        className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 transition-colors ${
                          i === activeIndex
                            ? 'bg-slate-100 text-slate-900'
                            : 'text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        <MapPin size={13} className="text-slate-400 shrink-0" />
                        {s.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <Button
                type="submit"
                disabled={loading || locationInput.trim().length === 0}
              >
                {loading ? <Loader2 className="animate-spin" size={16} /> : 'Go'}
              </Button>
            </div>
          </form>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-slate-500">or</span>
            </div>
          </div>

          {/* Use My Location */}
          <Button
            variant="outline"
            className="w-full"
            onClick={handleUseMyLocation}
            disabled={geoLoading}
          >
            {geoLoading ? (
              <Loader2 className="animate-spin" size={16} />
            ) : (
              <Navigation size={16} />
            )}
            Use My Current Location
          </Button>

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-lg">
              <X size={16} />
              {error}
            </div>
          )}

          {/* Reset to Default */}
          {isOverridden && (
            <Button
              variant="ghost"
              className="w-full text-slate-500"
              onClick={handleReset}
            >
              <RotateCcw size={14} />
              Reset to Auto-Detect
            </Button>
          )}

          {/* Current Location Display */}
          {currentLocation && (
            <div className="text-xs text-slate-500 text-center pt-2 border-t border-slate-100">
              Currently using: {currentLocation.label}
              {currentLocation.source === 'ipinfo' && ' (auto-detected)'}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
